import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import usePageVisibility from '@/hooks/UsePageVisibility';
import ReactDOM from 'react-dom';
import GenericTable, {
  GenericTableHandle,
} from '@/components/school-management/GenericTable/GenericTable';
import styles from './styles.module.scss';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import Button from '@/components/school-management/Button/Button';
import StudentDataModal from '@/components/school-management/StudentDataModal/StudentDataModal';
import { EditData } from '@/components/school-management/StudentDataModal/EditContainer/EditContainer';
import {
  StudentReportItem,
  StudentsReportQueryParams,
  StudentsReportSortByQueryParams,
} from '@/school-management/types/response';
import {
  DateFilterValue,
  FilterValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import { NumberRangeFilterValue } from '@/components/school-management/BimloaFilter/NumberRangeFilter';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import commonUtils from '@/school-management/utils/common';
import { studentService } from '@/school-management/services/student';
import { convertDateFilter } from '@/school-management/utils/filterUtils';
import config from '@/config';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import {
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
  GroupAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import {
  getStudentsColumnsData,
  StudentsTableColumnsVariant,
} from '@/components/school-management/StudentMetricsDashboard/normalizeData';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';
import { UserStatusEnum } from '@/school-management/types/response/studentDetails';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import {
  filterStudentReportItems,
  sortStudentReportItems,
} from '@/school-management/utils/studentReportClientFilter';
import ActionNotification from '@/components/school-management/ActionNotification/ActionNotification';
import MoveStudentsPopup from '@/components/school-management/BulkAction/MoveStudents/MoveStudentsPopup';
import DeleteStudentsPopup from '@/components/school-management/BulkAction/DeleteStudents/DeleteStudentsPopup';
import ChangeLevelPopup from '@/components/school-management/BulkAction/ChangeLevel/ChangeLevelPopup';

const PAGE_SIZE = config.studentsPageSize;
const MAX_BULK_SELECTED_STUDENTS = 50;
const REFETCH_ON_RETURN_COOLDOWN_MS = 30_000;

interface StudentsProps {
  data?: StudentReportItem[];
  schoolId?: string;
  studentFilters?: Record<string, FilterValue>;
  setStudentFilters: React.Dispatch<React.SetStateAction<Record<string, FilterValue>>>;
  hasAnyFilter?: boolean;
  toolsContainer?: React.MutableRefObject<any>;
  onStudentsLoaded?: (students: StudentReportItem[]) => void;
  onHasStudentsChange?: (hasStudents: boolean) => void;
  setShowStudentsStatistics: React.Dispatch<React.SetStateAction<boolean>>;
  setShowStudentsStatsSection?: React.Dispatch<React.SetStateAction<boolean>>;
  /** classView: Score column instead of first/last login (e.g. View Class page). */
  columnsVariant?: StudentsTableColumnsVariant;
  studentExportHandlerRef?: React.MutableRefObject<(() => void) | null>;
  /** `server`: students report API. `client`: rows from `clientStudents` (e.g. teacher overview). */
  studentsDataMode?: 'server' | 'client';
  /** When `studentsDataMode` is `client`, full list; `null` means parent is still loading. */
  clientStudents?: StudentReportItem[] | null;
  /** After student modal save/delete, refetch teacher overview / invalidate client list. */
  onClientDataInvalidate?: () => void;
  refreshSchoolDetails?: () => void;
  onStudentsSelected?: (selectedStudents: StudentReportItem[]) => void;
  onRefreshData?: () => void;
}

const Students: React.FC<StudentsProps> = ({
  data: propData,
  schoolId: propSchoolId,
  studentFilters,
  setStudentFilters,
  hasAnyFilter,
  toolsContainer,
  onStudentsLoaded,
  onHasStudentsChange,
  setShowStudentsStatistics,
  setShowStudentsStatsSection,
  columnsVariant = 'default',
  studentExportHandlerRef,
  studentsDataMode = 'server',
  clientStudents = null,
  onClientDataInvalidate,
  refreshSchoolDetails,
  onStudentsSelected,
  onRefreshData,
}) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const [selectedStudentData, setSelectedStudentData] = useState<{
    schoolId: string;
    studentId: string;
    level: number;
    firstLogin?: string | null;
  } | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [students, setStudents] = useState<StudentReportItem[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<{
    totalPages: number;
    totalCount: number;
  }>({
    totalPages: 1,
    totalCount: 0,
  });
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [displayDataForCurrentLevel, setDisplayDataForCurrentLevel] = useState<boolean>(true);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [bulkSelectedStudents, setBulkSelectedStudents] = useState<StudentReportItem[]>([]);
  const [bulkSelectedActivedStudents, setBulkSelectedActivedStudents] = useState<
    StudentReportItem[]
  >([]);
  const [hasSelectedStudents, setHasSelectedStudents] = useState<boolean>(false);
  const [hasSelectedActivedStudents, setHasSelectedActivedStudents] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const hasActiveSearch = searchQuery.trim().length > 0;
  const tableRef = useRef<GenericTableHandle<StudentReportItem>>(null);

  const schoolId = propSchoolId || user?.metadata?.organizationDetails?.organizationId || '';
  const isClientData = studentsDataMode === 'client';

  const filteredSortedClient = useMemo(() => {
    if (!isClientData || clientStudents === null) return [];
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const searchFilteredStudents = normalizedSearchQuery
      ? clientStudents.filter(student => {
          const searchableValues = [student.name, student.email || '', student.username || ''];
          return searchableValues.some(value =>
            value.toLowerCase().includes(normalizedSearchQuery)
          );
        })
      : clientStudents;

    const filtered = filterStudentReportItems(searchFilteredStudents, studentFilters, t);
    return sortStudentReportItems(filtered, sortBy, sortDirection);
  }, [isClientData, clientStudents, studentFilters, t, sortBy, sortDirection, searchQuery]);

  const paginationInfoClient = useMemo(() => {
    const total = filteredSortedClient.length;
    return {
      totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE) || 1),
      totalCount: total,
    };
  }, [filteredSortedClient]);

  const pagedClientRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSortedClient.slice(start, start + PAGE_SIZE);
  }, [filteredSortedClient, currentPage]);

  const buildStudentsQueryParams = useCallback(
    (page: number): StudentsReportQueryParams => {
      const queryParams: StudentsReportQueryParams = {
        page,
        pageSize: PAGE_SIZE,
        filterByCurrentLevel: studentFilters?.displayDataFor === 'currentLevel',
      };

      if (sortBy) {
        queryParams.sortBy = sortBy as StudentsReportSortByQueryParams;
        queryParams.sortDirection = sortDirection;
      }

      const normalizedSearchQuery = searchQuery.trim();
      if (normalizedSearchQuery) {
        queryParams.search = normalizedSearchQuery;
      }

      if (studentFilters?.name) {
        const studentIds = (studentFilters.name as StudentAutocompleteItem[]).map(s => s.id);
        if (studentIds.length > 0) {
          queryParams.studentIds = studentIds;
        }
      }

      if (studentFilters?.teacher) {
        const teacherIds = (studentFilters.teacher as TeacherAutocompleteItem[]).map(t => t.id);
        if (teacherIds.length > 0) {
          queryParams.teacherIds = teacherIds;
        }
      }

      if (studentFilters?.groupName) {
        const groupIds = (studentFilters.groupName as GroupAutocompleteItem[]).map(g => g.groupId);
        if (groupIds.length > 0) {
          queryParams.groupIds = groupIds;
        }
      }

      const grades = (studentFilters?.gradeName || []) as string[];
      if (grades.length > 0) {
        const gradesLabelToIdMapper = commonUtils.getGradesLabelToIdMapper(t);
        queryParams.gradeIds = grades.map(g => gradesLabelToIdMapper[g]);
      }

      const status = ((studentFilters?.status || []) as string[]).filter(v => v && v !== 'All');
      if (status.length > 0) {
        const statusMapper = {
          [t('active')]: [
            UserStatusEnum.ACTIVE,
            UserStatusEnum.LEVEL_DOWN,
            UserStatusEnum.LEVEL_UP,
            UserStatusEnum.LEVEL_COMPLETED_BLOCKED,
          ],
          [t('inactive')]: [UserStatusEnum.INACTIVE],
          [t('levelTestIncomplete')]: [UserStatusEnum.LEVEL_TEST_INCOMPLETE],
          [t('resetPassword')]: [UserStatusEnum.RESET_PASSWORD],
        };

        queryParams.userStatuses = status.reduce((acc, s) => {
          const status = statusMapper[s] || [];
          acc = [...acc, ...status];
          return acc;
        }, [] as UserStatusEnum[]);
      }

      const startingLevel = ((studentFilters?.startingLevel || []) as string[])
        .filter(v => v && v !== 'All')
        .map(v => Number(v));
      const currentLevel = ((studentFilters?.currentLevel || []) as string[])
        .filter(v => v && v !== 'All')
        .map(v => Number(v));
      if (startingLevel.length > 0 || currentLevel.length > 0) {
        queryParams.levels = {};

        if (startingLevel.length > 0) {
          queryParams.levels.startingList = startingLevel;
        }

        if (currentLevel.length > 0) {
          queryParams.levels.currentList = currentLevel;
        }
      }

      if (
        studentFilters?.completedActivities &&
        typeof studentFilters.completedActivities === 'object' &&
        !Array.isArray(studentFilters.completedActivities) &&
        (typeof (studentFilters.completedActivities as NumberRangeFilterValue).min === 'number' ||
          typeof (studentFilters.completedActivities as NumberRangeFilterValue).max === 'number')
      ) {
        const rangeFilter = studentFilters.completedActivities as NumberRangeFilterValue;
        if (typeof rangeFilter.min === 'number' && typeof rangeFilter.max === 'number') {
          queryParams.activities = {
            completedRange: { min: rangeFilter.min, max: rangeFilter.max },
          };
        } else if (typeof rangeFilter.min === 'number') {
          queryParams.activities = { completedRange: { min: rangeFilter.min, max: 2147483647 } };
        } else if (typeof rangeFilter.max === 'number') {
          queryParams.activities = { completedRange: { min: 0, max: rangeFilter.max } };
        }
      }

      if (
        studentFilters?.score &&
        typeof studentFilters.score === 'object' &&
        !Array.isArray(studentFilters.score) &&
        (typeof (studentFilters.score as NumberRangeFilterValue).min === 'number' ||
          typeof (studentFilters.score as NumberRangeFilterValue).max === 'number')
      ) {
        const rangeFilter = studentFilters.score as NumberRangeFilterValue;
        if (typeof rangeFilter.min === 'number' && typeof rangeFilter.max === 'number') {
          queryParams.score = { from: rangeFilter.min, to: rangeFilter.max };
        } else if (typeof rangeFilter.min === 'number') {
          queryParams.score = { from: rangeFilter.min, to: 100 };
        } else if (typeof rangeFilter.max === 'number') {
          queryParams.score = { from: 0, to: rangeFilter.max };
        }
      }

      if (
        studentFilters?.firstLogin &&
        typeof studentFilters.firstLogin === 'object' &&
        'mode' in studentFilters.firstLogin
      ) {
        const dateFilter = convertDateFilter(studentFilters.firstLogin as DateFilterValue);
        if (dateFilter) {
          queryParams.loginTimes = { ...queryParams.loginTimes };
          if (dateFilter.exact) {
            queryParams.loginTimes.firstLoginExact = dateFilter.exact;
          }
          if (dateFilter.range) {
            queryParams.loginTimes.firstLoginRange = dateFilter.range;
          }
        }
      }

      if (
        studentFilters?.lastLogin &&
        typeof studentFilters.lastLogin === 'object' &&
        'mode' in studentFilters.lastLogin
      ) {
        const dateFilter = convertDateFilter(studentFilters.lastLogin as DateFilterValue);
        if (dateFilter) {
          queryParams.loginTimes = { ...queryParams.loginTimes };
          if (dateFilter.exact) {
            queryParams.loginTimes.lastLoginExact = dateFilter.exact;
          }
          if (dateFilter.range) {
            queryParams.loginTimes.lastLoginRange = dateFilter.range;
          }
        }
      }

      return queryParams;
    },
    [studentFilters, sortBy, sortDirection, searchQuery]
  );

  // Ignore responses from older requests so a slower unfiltered call can't overwrite a faster filtered result
  const fetchRequestIdRef = React.useRef(0);
  const { isVisible: isPageVisible } = usePageVisibility();
  const wasPageVisibleRef = React.useRef(isPageVisible);
  const currentPageRef = React.useRef(currentPage);

  React.useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Fetch students with pagination
  const fetchStudents = useCallback(
    async (page: number) => {
      if (!schoolId) return;

      fetchRequestIdRef.current += 1;
      const requestId = fetchRequestIdRef.current;

      setIsLoadingStudents(true);
      setStudentsError(null);
      try {
        const queryParams = buildStudentsQueryParams(page);
        const response = await studentService.getAllStudents(schoolId, queryParams);

        if (requestId !== fetchRequestIdRef.current) return;

        setStudents(response.data.items);
        setPaginationInfo({
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
        });

        if (onStudentsLoaded) {
          onStudentsLoaded(response.data.items);
        }
      } catch (error: any) {
        if (requestId !== fetchRequestIdRef.current) return;
        setStudentsError(error?.message || t('schoolDetailsPage.students.failedToFetch'));
        setStudents([]);
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setIsLoadingStudents(false);
          setIsFirstLoading(false);
        }
      }
    },
    [schoolId, buildStudentsQueryParams, t]
  );

  const shouldFetchFirstPageRef = React.useRef(false);

  React.useEffect(() => {
    if (isClientData) return;
    setCurrentPage(1);
    shouldFetchFirstPageRef.current = true;
  }, [studentFilters, isClientData]);

  React.useEffect(() => {
    if (isClientData) return;
    const pageToFetch = shouldFetchFirstPageRef.current ? 1 : currentPage;
    if (shouldFetchFirstPageRef.current) shouldFetchFirstPageRef.current = false;
    fetchStudents(pageToFetch);
  }, [currentPage, fetchStudents, isClientData]);

  const lastRefetchOnReturnRef = React.useRef(0);

  const refetchStudentsOnReturn = React.useCallback(() => {
    if (isClientData) return;
    const now = Date.now();
    if (now - lastRefetchOnReturnRef.current < REFETCH_ON_RETURN_COOLDOWN_MS) return;
    lastRefetchOnReturnRef.current = now;
    void fetchStudents(currentPageRef.current);
  }, [isClientData, fetchStudents]);

  React.useEffect(() => {
    if (isPageVisible && !wasPageVisibleRef.current) {
      refetchStudentsOnReturn();
    }
    wasPageVisibleRef.current = isPageVisible;
  }, [isPageVisible, refetchStudentsOnReturn]);

  React.useEffect(() => {
    if (!isClientData) return;
    if (clientStudents !== null) {
      setIsFirstLoading(false);
      setIsLoadingStudents(false);
      setStudentsError(null);
    } else {
      setIsFirstLoading(true);
      setIsLoadingStudents(true);
    }
  }, [isClientData, clientStudents]);

  React.useEffect(() => {
    if (!isClientData) return;
    setCurrentPage(1);
  }, [studentFilters, isClientData]);

  React.useEffect(() => {
    setShowStudentsStatsSection?.(
      isFirstLoading ||
        isLoadingStudents ||
        (students && students.length > 0) ||
        !!hasAnyFilter ||
        hasActiveSearch
    );
  }, [
    setShowStudentsStatsSection,
    students,
    hasAnyFilter,
    isLoadingStudents,
    isFirstLoading,
    hasActiveSearch,
  ]);

  React.useEffect(() => {
    if (isFirstLoading) {
      setShowStudentsStatistics(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowStudentsStatistics(
        isLoadingStudents || (students && students.length > 0) || !!hasAnyFilter || hasActiveSearch
      );
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    setShowStudentsStatistics,
    students,
    hasAnyFilter,
    isLoadingStudents,
    isFirstLoading,
    hasActiveSearch,
  ]);

  useEffect(() => {
    setStudentFilters(prev => {
      if (!prev) {
        return prev;
      }
      const nextDisplay = displayDataForCurrentLevel ? 'currentLevel' : 'allLevels';
      if (prev.displayDataFor === nextDisplay) {
        return prev;
      }
      return {
        ...prev,
        displayDataFor: nextDisplay,
      };
    });
  }, [displayDataForCurrentLevel, setStudentFilters]);

  const paginationInfoActive = isClientData ? paginationInfoClient : paginationInfo;

  const data = isClientData ? pagedClientRows : students || [];

  const hasStudentsInSystem: boolean =
    paginationInfoActive?.totalCount !== undefined && paginationInfoActive.totalCount > 0;

  const displayDataForFilter = useMemo(() => {
    if (!hasStudentsInSystem) return <></>;
    if (toolsContainer?.current) {
      const options: Option[] = [
        {
          label: t('schoolDetailsPage.students.currentLevel'),
          value: 'currentLevel',
        },
        {
          label: t('schoolDetailsPage.students.allLevels'),
          value: 'allLevels',
        },
      ];

      return ReactDOM.createPortal(
        <div className={styles.displayDataForFilter}>
          <DropDown
            options={options}
            value={displayDataForCurrentLevel ? options[0] : options[1]}
            onChange={value => {
              setDisplayDataForCurrentLevel(value.value === 'currentLevel');
            }}
            staticTitle={`${t('schoolDetailsPage.students.displayDataFor') || ''}:`}
            containerClassName={styles.displayDataForFilterDropDown}
            isEmptyBorder={true}
            autoClose={true}
            disabled={hasSelectedStudents}
          />
        </div>,
        toolsContainer.current
      );
    }

    return <></>;
  }, [
    toolsContainer,
    toolsContainer?.current,
    displayDataForCurrentLevel,
    t,
    hasSelectedStudents,
    hasStudentsInSystem,
  ]);

  React.useEffect(() => {
    if (onHasStudentsChange) {
      onHasStudentsChange(hasStudentsInSystem);
    }
  }, [hasStudentsInSystem, onHasStudentsChange]);

  const columns = getStudentsColumnsData(t, user, columnsVariant);
  columns[columns.length - 1] = {
    key: 'actions',
    label: '',
    width: '55px',
    render: (value, row) => (
      <Button
        secondary
        className={styles.arrowBtn}
        onClick={() => {
          setSelectedStudentData({
            schoolId: schoolId || '',
            studentId: row.userId,
            level: row.currentLevel,
            firstLogin: row.firstLogin,
          });
        }}
      >
        <AppIcon icon='arrowRight' width={24} height={24} />
      </Button>
    ),
  };

  const tableName = useMemo(() => {
    return t(`schoolDetailsPage.students.title`) || '';
  }, [t]);

  const onDownload = useCallback(async () => {
    console.log('Clicked to download students CSV');

    let classesData: StudentReportItem[] = [];

    if (isClientData) {
      classesData = filteredSortedClient;
    } else if (schoolId) {
      try {
        const pageQueryParam = buildStudentsQueryParams(1);
        pageQueryParam.page = 0;
        let hasNextPage: boolean = true;

        while (hasNextPage) {
          pageQueryParam.page++;
          const response = await studentService.getAllStudents(schoolId, pageQueryParam);

          if (response?.data?.items && response.data.items.length > 0) {
            classesData = [...classesData, ...response.data.items];
          }

          hasNextPage = !!response?.data?.hasNextPage;
        }
      } catch (er) {}
    }

    const xlsxColumns = columns.filter(c => !!c.xlsxRender);
    const headerRow = xlsxColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };

    const skillTable: XlsxTable = {
      name: tableName,
      headerRow: [...headerRow],
      data: [],
    };
    classesData.forEach(l => {
      const row: any = {};
      xlsxColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);

    processing.createXlsx(xlsxData);
  }, [schoolId, buildStudentsQueryParams, columns, tableName, isClientData, filteredSortedClient]);

  const isActivedStudent = useCallback((student: StudentReportItem) => {
    return (
      student.userStatus &&
      student.userStatus !== UserStatusEnum.INACTIVE &&
      student.userStatus !== UserStatusEnum.LEVEL_TEST_INCOMPLETE
    );
  }, []);

  const onBulkAction = useCallback(
    (actionKey: string, selectedStudents: StudentReportItem[]) => {
      switch (actionKey) {
        case 'delete_students':
        case 'move_students':
        case 'change_level':
          setBulkSelectedStudents(selectedStudents);
          setBulkSelectedActivedStudents(
            selectedStudents.filter(student => isActivedStudent(student))
          );
          setBulkAction(actionKey);
          break;
      }
    },
    [isActivedStudent]
  );

  const refreshData = useCallback(
    (page?: number) => {
      if (isClientData) {
        onClientDataInvalidate?.();
      } else if (page) {
        setCurrentPage(prevPage => {
          if (prevPage === page) {
            fetchStudents(page);
          }

          return page;
        });
      } else void fetchStudents(currentPage);

      onRefreshData?.();
    },
    [onClientDataInvalidate, fetchStudents, currentPage, onRefreshData]
  );

  const onBulkActionEnd = useCallback(
    (moveedStudents: StudentReportItem[]) => {
      setBulkAction('');

      if (moveedStudents.length > 0) {
        const moveedStudentsMapper = moveedStudents.reduce(
          (acc, s) => {
            acc[s.userId] = true;
            return acc;
          },
          {} as {
            [userId: string]: boolean;
          }
        );

        if (tableRef.current) {
          const notMoveedStudents = (tableRef.current.getSelectedItems() || []).filter(
            s => !moveedStudentsMapper[s.userId]
          );
          tableRef.current.setSelectedItems(notMoveedStudents);
        }

        refreshData(1);

        if (bulkAction === 'delete_students') {
          refreshSchoolDetails?.();
        }
      }
    },
    [refreshData, bulkAction, refreshSchoolDetails]
  );

  useEffect(() => {
    if (!studentExportHandlerRef) return;
    studentExportHandlerRef.current = onDownload;
    return () => {
      studentExportHandlerRef.current = null;
    };
  }, [studentExportHandlerRef, onDownload]);

  const isEmpty = !hasStudentsInSystem && data.length === 0;

  const showTableLoader = isClientData
    ? clientStudents === null
    : isFirstLoading || isLoadingStudents;

  return (
    <div className={`${styles.teachersMode} ${isEmpty ? styles.emptyStateMode : ''}`}>
      {displayDataForFilter}
      <div className={`${styles.teacherDetailsCard} ${isEmpty ? styles.emptyStateCard : ''}`}>
        <GenericTable
          levelHint={t('schoolDetailsPage.students.allLevelsHint') || ''}
          showSearch={true}
          showHeaderInEmptyState={showTableLoader || hasAnyFilter || hasActiveSearch}
          searchPlaceholder={t('schoolDetailsPage.students.searchPlaceholder') || 'Search students'}
          onSearchQueryChange={query => {
            setSearchQuery(query);

            if ((query || '').trim()) {
              setSortBy('');
            } else if (!sortBy) {
              setSortBy('name');
              setSortDirection('asc');
            }

            setCurrentPage(1);
          }}
          showLoader={showTableLoader}
          columns={columns}
          data={data}
          rowKey={s => s.userId}
          variant='compact'
          className={styles.classesTable}
          pageSize={PAGE_SIZE}
          serverSideDownload={true}
          showSortArrows={true}
          title={tableName}
          subTitle={
            (data || []).find(s => s.isStartingLevelChangedByTeacher)
              ? t('schoolDetailsPage.students.subTitle') || ''
              : ''
          }
          onDownload={onDownload}
          downloadFileName={tableName}
          hasData={hasAnyFilter || data.length > 0}
          emptyStateTitle={
            showTableLoader || hasAnyFilter || hasActiveSearch
              ? ' '
              : t('schoolDetailsPage.students.zeroState.title') || ''
          }
          emptyStateDescription={
            showTableLoader
              ? ' '
              : hasActiveSearch
                ? t('genericTable.noResultsForSearchQuery', {
                    searchQuery: searchQuery,
                  }) || ''
                : hasAnyFilter
                  ? t('schoolDetailsPage.students.zeroState.filterTitle') || ''
                  : t('schoolDetailsPage.students.zeroState.description') || ''
          }
          emptyStateSubDescription={
            hasAnyFilter && !showTableLoader
              ? t('schoolDetailsPage.students.zeroState.filterDescription') || ''
              : undefined
          }
          serverSidePagination={true}
          totalPages={paginationInfoActive.totalPages}
          totalCount={paginationInfoActive.totalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          serverSideSorting={true}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => {
            if (newSortBy && newSortDirection) {
              setSortBy(newSortBy);
              setSortDirection(newSortDirection);
            }
          }}
          isMultiSelect={true}
          maxSelectItems={MAX_BULK_SELECTED_STUDENTS}
          titleRenderOnSelected={num =>
            t('schoolDetailsPage.students.titleOnSelected', {
              num,
              count: num,
              maxNum: MAX_BULK_SELECTED_STUDENTS,
            })
          }
          selectedActions={[
            {
              text: (count: number) => t('schoolDetailsPage.students.deleteStudents', { count }),
              key: 'delete_students',
            },
            {
              text: (count: number) => t('schoolDetailsPage.students.changeLevel', { count }),
              key: 'change_level',
              hidden: !hasSelectedActivedStudents,
            },
            {
              text: (count: number) => t('schoolDetailsPage.students.moveStudents', { count }),
              key: 'move_students',
            },
          ]}
          onAction={onBulkAction}
          onItemsSelect={(items: StudentReportItem[]) => {
            onStudentsSelected?.(items);
            setHasSelectedStudents(items.length > 0);
            setHasSelectedActivedStudents(
              items.filter(student => isActivedStudent(student)).length > 0
            );
          }}
          ref={tableRef}
        />
      </div>
      {studentsError && (
        <div>{t('schoolDetailsPage.students.errorLoading', { error: studentsError })}</div>
      )}

      <StudentDataModal
        schoolId={selectedStudentData?.schoolId}
        studentId={selectedStudentData?.studentId}
        level={selectedStudentData?.level}
        firstLogin={selectedStudentData?.firstLogin}
        onClose={() => {
          setSelectedStudentData(null);
        }}
        onEdit={(studentId: string, newData: EditData, oldData: EditData) => {
          refreshData();
        }}
        onDelete={(studentId: string) => {
          if (tableRef.current) {
            const notDeletedStudents = (tableRef.current.getSelectedItems() || []).filter(
              s => s.userId !== studentId
            );
            tableRef.current.setSelectedItems(notDeletedStudents);
          }

          setNotifMessage(t('dynamicForm.notification.studentDeleted') || '');
          setNotifType('success');
          setNotifOpen(true);
          refreshData();
          refreshSchoolDetails?.();
        }}
      />
      <ActionNotification
        open={notifOpen}
        message={notifMessage}
        type={notifType}
        position='top-center'
        onClose={() => setNotifOpen(false)}
      />
      <MoveStudentsPopup
        show={bulkAction === 'move_students'}
        students={bulkSelectedStudents}
        onClose={onBulkActionEnd}
      />
      <DeleteStudentsPopup
        show={bulkAction === 'delete_students'}
        students={bulkSelectedStudents}
        onClose={onBulkActionEnd}
      />
      <ChangeLevelPopup
        show={bulkAction === 'change_level'}
        students={bulkSelectedActivedStudents}
        hasNonActivedStudents={bulkSelectedStudents.length !== bulkSelectedActivedStudents.length}
        onClose={onBulkActionEnd}
      />
    </div>
  );
};

export default Students;
